import { React, useState } from 'react';
import { Link } from 'react-router-dom';

// src/pages/xray/XrayLobby/XrayLobby.js
import UploadSection from './UploadSection';
import DeviceOverview from './DeviceOverview';
import TabsManager from './TabsManager';
import TabPhysicalPorts from './TabPhysicalPorts.js';
import TabVLANs from './TabVLANs.js';
import TabPacketsDropped from './TabPacketsDropped.js';
import TabWanFailover from './TabWanFailover.js';
import TabRouting from './TabRouting.js';
import TabARP from './TabARP.js';
import TabDHCP from './TabDHCP.js';
import TabProtocolSIP from './TabProtocolSIP.js';
import TabTrafficPass from './TabTrafficPass.js';
import TabEdgeview from './TabEdgeview.js';
import TabErrors from './TabErrors.js';
import TabUserCommands from './TabUserCommands';
import TabVosImageVersion from './TabVosImageVersion';
import { processFileLines } from './utils';

const XrayLobby = () => {

  const [fileName, setFileName] = useState('No file selected');
  const [parsedData, setParsedData] = useState({});
  const [selectedTab, setSelectedTab] = useState('tabs-level1');
  const [selectedTabsPhysicalPorts, setSelectedTabsPhysicalPorts] =
    useState('tabsPhysicalPorts1');
  const [selectedTabsVLANs, setSelectedTabsVLANs] = useState('tabsVLANs1');
  const [selectedTabsPackets, setSelectedTabsPackets] =
    useState('tabsPackets1');
  const [selectedTabsWanFailover, setSelectedTabsWanFailover] =
    useState('tabsWanFailover1');
  const [selectedTabsRouting, setSelectedTabsRouting] =
    useState('tabsRouting1');
  const [selectedTabsARP, setSelectedTabsARP] = useState('tabsARP1');
  const [selectedUserCOMMANDS, setSelectedUserCOMMANDS] =
    useState('userCOMMANDS1');
  const [selectedVosVERSION, setSelectedVosVERSION] = useState('vosVERSION1');
  const [selectedTabsLeasesDHCP, setSelectedTabsLeasesDHCP] =
    useState('tabsLeasesDHCP1');
  const [selectedTabsSIP, setSelectedTabsSIP] = useState('tabsSIP1');
  const [selectedTabsPassthrough, setSelectedTabsPassthrough] =
    useState('tabsPassthrough1');
  const [selectedTabsEdgeview, setSelectedTabsEdgeview] =
    useState('tabsEdgeview1');
  const [selectedTabsErrors, setSelectedTabsErrors] = useState('tabsErrors1');

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      readFile(file);
    }
  };

  const readFile = file => {
    const reader = new FileReader();
    reader.onload = event => {
      const content = event.target.result;
      const parsedData = processFileLines(content);
      setParsedData(parsedData);
    };
    reader.readAsText(file);
  };

  const spinnerWrapperStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  };

  return (
    <div className="flex flex-col p-6 bg-gray-100 gap-6 h-screen">
      <div className="flex p-6 bg-gray-100 gap-6">
        <UploadSection
          fileName={fileName}
          handleFileChange={handleFileChange}
        />
        <div className="w-4/5 bg-white shadow-lg rounded-lg p-6 border">
          <h1 className="text-2xl font-semibold">
            Device <span className="text-red-600">Overview</span>
          </h1>
          <DeviceOverview deviceOverview={parsedData.deviceOverview} />
        </div>
      </div>

      <div className="h-screen">
        <TabsManager
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />

        <div className="px-6 py-2 bg-white shadow-lg rounded-lg">
          {selectedTab === 'tabs-level1' && (
            <TabPhysicalPorts
              selectedTabsPhysicalPorts={selectedTabsPhysicalPorts}
              setSelectedTabsPhysicalPorts={setSelectedTabsPhysicalPorts}
              sfpStatus={parsedData.sfpStatus}
              wanStatus={parsedData.wanStatus}
              lanStatus={parsedData.lanStatus}
            />
          )}

          {selectedTab === 'tabs-level2' && (
            <TabVLANs
              selectedTabsVLANs={selectedTabsVLANs}
              setSelectedTabsVLANs={setSelectedTabsVLANs}
              defaultVLAN={parsedData.defaultVLANLines}
              voiceVLAN={parsedData.deviceOverview?.['Voice vlan']}
              membersVLAN={parsedData.membersVLAN}
              tagVLAN={parsedData.tagVLAN}
              vlanNetworks={parsedData.vlanNetworks}
            />
          )}

          {selectedTab === 'tabs-level3' && (
            <TabPacketsDropped
              selectedTabsPackets={selectedTabsPackets}
              setSelectedTabsPackets={setSelectedTabsPackets}
              packetsDropped={parsedData.packetsDropped}
            />
          )}

          {selectedTab === 'tabs-level4' && (
            <TabWanFailover
              selectedTabsWanFailover={selectedTabsWanFailover}
              setSelectedTabsWanFailover={setSelectedTabsWanFailover}
              priWanFailoverLines={parsedData.priWanFailoverLines}
              secWanFailoverLines={parsedData.secWanFailoverLines}
              voiceVsDataTraffic={parsedData.voiceVsDataTraffic}
            />
          )}

          {selectedTab === 'tabs-level5' && (
            <TabRouting
              selectedTabsRouting={selectedTabsRouting}
              setSelectedTabsRouting={setSelectedTabsRouting}
              routingTable={parsedData.routingTable}
              cidrRanges={parsedData.cidrRanges}
            />
          )}

          {selectedTab === 'tabs-level6' && (
            <TabARP
              selectedTabsARP={selectedTabsARP}
              setSelectedTabsARP={setSelectedTabsARP}
              arpTable={parsedData.arpTable}
              formattedArpTable={parsedData.formattedArpTable}
            />
          )}

          {selectedTab === 'tabs-level7' && (
            <TabDHCP
              selectedTabsLeasesDHCP={selectedTabsLeasesDHCP}
              setSelectedTabsLeasesDHCP={setSelectedTabsLeasesDHCP}
              scopesDHCPLimits={parsedData.scopesDHCPLimits}
              formattedDhcpLeases={parsedData.formattedDhcpLeases}
            />
          )}

          {selectedTab === 'tabs-level8' && (
            <TabProtocolSIP
              selectedTabsSIP={selectedTabsSIP}
              setSelectedTabsSIP={setSelectedTabsSIP}
              sipServerLimits={parsedData.sipServerLimits}
              sipClientsLimits={parsedData.sipClientsLimits}
              sipProxyPort={parsedData.deviceOverview?.['SIP Proxy Port']}
            />
          )}

          {selectedTab === 'tabs-level9' && (
            <TabTrafficPass
              selectedTabsPassthrough={selectedTabsPassthrough}
              setSelectedTabsPassthrough={setSelectedTabsPassthrough}
              proxyARPRulesLimits={parsedData.proxyARPRulesLimits}
              passthroughRulesLimits={parsedData.passthroughRulesLimits}
            />
          )}

          {selectedTab === 'tabs-level10' && (
            <TabEdgeview
              selectedTabsEdgeview={selectedTabsEdgeview}
              setSelectedTabsEdgeview={setSelectedTabsEdgeview}
              syslogHostStatus={parsedData.syslogHostStatus}
            />
          )}

          {selectedTab === 'tabs-level11' && (
            <TabErrors
              selectedTabsErrors={selectedTabsErrors}
              setSelectedTabsErrors={setSelectedTabsErrors}
              loggedErrorsLimits={parsedData.loggedErrorsLimits}
              conntrackTableLimits={parsedData.conntrackTableLimits}
            />
          )}
          {selectedTab === 'tabs-level12' && (
            <TabUserCommands
              selectedUserCOMMANDS={selectedUserCOMMANDS}
              setSelectedUserCOMMANDS={setSelectedUserCOMMANDS}
              userCommandsLimits={parsedData.userCommandsLimits}
            />
          )}
          {selectedTab === 'tabs-level13' && (
            <TabVosImageVersion
              selectedVosVERSION={selectedVosVERSION}
              setSelectedVosVERSION={setSelectedVosVERSION}
              vosImgVersionLimits={parsedData.vosImgVersionLimits}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default XrayLobby;
